import {useMemo} from "react";
import type {GroupBase, StylesConfig, Theme as ReactSelectTheme} from "react-select";

import {IOption} from "../types/IOption";

export interface IHook<Value, Option extends IOption<Value>, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>> {
    getTheme: (theme: ReactSelectTheme) => ReactSelectTheme;
    styles: StylesConfig<Option, IsMulti, Group>;
}

export const useSelectTheme = <Value, Option extends IOption<Value>, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    isBorderless?: boolean
): IHook<Value, Option, IsMulti, Group> => {
    const getTheme = ({colors, spacing}: ReactSelectTheme): ReactSelectTheme => {
        return {
            colors: {
                ...colors,
                neutral20: "#a7a7ab",
                neutral30: "#a7a7ab",
                primary: "#9bd7ff",
                primary25: "#ededee"
            },
            spacing: {...spacing, controlHeight: 48},
            borderRadius: 8
        };
    };

    const styles: StylesConfig<Option, IsMulti, Group> = useMemo(() => {
        return {
            control: (provided, state) => {
                return {
                    ...provided,
                    backgroundColor: state.isDisabled ? "#f9f9f9" : provided.backgroundColor,
                    borderColor: state.isDisabled ? "#a7a7ab" : provided.borderColor,
                    ["&:hover"]: {...(provided["&:hover"] as object), boxShadow: "0px 4px 10px rgba(174, 177, 180, 0.3)"},
                    ...(isBorderless ? {border: "none", boxShadow: "none"} : null)
                };
            },
            indicatorSeparator: () => {
                return {display: "none"};
            },
            menu: (provided) => {
                return {...provided, padding: "0.8rem", zIndex: 1000, marginLeft: "1px", marginRight: "1px", width: "calc(100% - 2px)"};
            },
            noOptionsMessage: (provided) => {
                return {...provided, color: "#23232d", padding: "0.8rem"};
            },
            option: (provided, state) => {
                return {
                    ...provided,
                    backgroundColor: state.isSelected ? "#fff" : provided.backgroundColor,
                    color: state.isSelected ? "#23232d" : provided.color
                };
            },
            placeholder: (provided, state) => {
                return {...provided, color: state.isDisabled ? "#e5e5e6" : "#23232d"};
            },
            valueContainer: (provided) => {
                return {...provided, padding: "0 1.6rem"};
            }
        };
    }, []);

    return {
        getTheme,
        styles
    };
};
